/**
 * Metadata Service
 *
 * Fetches URL metadata using Microlink.io API
 * Free tier: 50 requests/day
 *
 * IMPORTANT: Only fetch metadata on CREATE, never auto-refresh
 */

import type { LinkMetadata } from '@/types';
import { detectLinkTypeSync, getYouTubeVideoIdSync, type LinkType } from './llmAnalysis';

// Re-export LinkType from llmAnalysis for backwards compatibility
export type { LinkType } from './llmAnalysis';

const MICROLINK_API = 'https://api.microlink.io';

interface MicrolinkResponse {
  status: 'success' | 'fail';
  data: {
    title?: string;
    description?: string;
    image?: { url: string };
    logo?: { url: string };
    publisher?: string;
  };
}

/**
 * Fetch metadata for a URL using Microlink.io
 *
 * @param url - URL to fetch metadata for
 * @returns Metadata object with title, description, image, favicon, siteName
 *
 * @example
 * const metadata = await fetchMetadata('https://example.com');
 * // => { title: 'Example', description: '...', image: '...', favicon: '...', siteName: 'Example' }
 */
export async function fetchMetadata(url: string): Promise<LinkMetadata> {
  try {
    const response = await fetch(`${MICROLINK_API}?url=${encodeURIComponent(url)}`);

    if (!response.ok) {
      console.warn(`Microlink API returned ${response.status}`);
      return createEmptyMetadata();
    }

    const data: MicrolinkResponse = await response.json();

    if (data.status !== 'success') {
      console.warn('Microlink API returned non-success status');
      return createEmptyMetadata();
    }

    return {
      title: data.data.title ?? null,
      description: data.data.description ?? null,
      image: data.data.image?.url ?? null,
      favicon: data.data.logo?.url ?? null,
      siteName: data.data.publisher ?? null,
    };
  } catch (error) {
    console.error('Failed to fetch metadata:', error);
    return createEmptyMetadata();
  }
}

/**
 * Create an empty metadata object
 */
function createEmptyMetadata(): LinkMetadata {
  return {
    title: null,
    description: null,
    image: null,
    favicon: null,
    siteName: null,
  };
}

/**
 * Extract YouTube video ID from URL.
 * Uses LLM-powered analysis service with fallback to simple heuristics.
 *
 * @param url - YouTube URL
 * @returns Video ID or null if not a YouTube URL
 */
export function getYouTubeVideoId(url: string): string | null {
  return getYouTubeVideoIdSync(url);
}

/**
 * Get YouTube thumbnail URL for a video
 *
 * @param url - YouTube URL
 * @returns Thumbnail URL or null if not a YouTube video
 *
 * @example
 * getYouTubeThumbnail('https://youtube.com/watch?v=dQw4w9WgXcQ')
 * // => 'https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg'
 */
export function getYouTubeThumbnail(url: string): string | null {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return null;
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

/**
 * Detect the type of link for special handling.
 * Uses LLM-powered analysis service with fallback to simple heuristics.
 *
 * @param url - URL to analyze
 * @returns Link type
 */
export function detectLinkType(url: string): LinkType {
  return detectLinkTypeSync(url);
}

/**
 * Check if a URL is likely a podcast episode
 *
 * @param url - URL to check
 * @returns True if the URL appears to be a podcast
 */
export function isPodcastUrl(url: string): boolean {
  return detectLinkType(url) === 'podcast';
}

/**
 * Get a favicon URL for a domain
 * Uses Google's favicon service as fallback
 *
 * @param url - URL to get favicon for
 * @returns Favicon URL
 */
export function getFaviconUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=64`;
  } catch {
    return '';
  }
}

/**
 * Extract domain name from URL for display
 *
 * @param url - URL to extract domain from
 * @returns Domain name without www prefix
 *
 * @example
 * getDomain('https://www.example.com/path') // => 'example.com'
 */
export function getDomain(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
